import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'
import { cn } from '@/lib/utils'
import {
    Save,
    FileDown,
    Loader2,
    History,
    Undo,
    Redo,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    MessageSquare,
} from 'lucide-react'

interface Script {
    id: string
    title: string
    author: string | null
    logline: string | null
    version: number
    content: ScriptContent
    status: string
    wordCount: number
    pageCount: number
    updatedAt: number
}

interface ScriptContent {
    elements: ScriptElement[]
    metadata: {
        title: string
        author: string
        copyright?: string
        contact?: string
        date?: string
        draft?: string
    }
}

interface ScriptElement {
    id: string
    type: ElementType
    content: string
    metadata?: Record<string, unknown>
}

type ElementType = 'scene_heading' | 'action' | 'character' | 'dialogue' | 'parenthetical' | 'transition' | 'shot' | 'note' | 'centered'

// Fountain format detection patterns
const SCENE_HEADING_PATTERN = /^(INT\.|EXT\.|INT\/EXT\.|I\/E\.|EST\.)/i
const TRANSITION_PATTERN = /^.*(CUT TO:|FADE TO:|FADE OUT\.|FADE IN:|DISSOLVE TO:|SMASH CUT TO:|MATCH CUT TO:|JUMP CUT TO:)$/i

const ELEMENT_LABELS: Record<ElementType, string> = {
    scene_heading: 'Scene',
    action: 'Action',
    character: 'Character',
    dialogue: 'Dialogue',
    parenthetical: 'Paren',
    transition: 'Transition',
    shot: 'Shot',
    note: 'Note',
    centered: 'Centered',
}

// Tab only cycles between Action and Character
const TAB_CYCLE: ElementType[] = ['action', 'character']

// Page sizing - larger pages that fill more viewport
const PAGE_WIDTH_PX = 900  // Wider to fill more space
const PAGE_HEIGHT_PX = 1150 // Taller
const PAGE_PADDING_TOP = 64
const PAGE_PADDING_BOTTOM = 64
const PAGE_PADDING_LEFT = 96
const PAGE_PADDING_RIGHT = 64
const LINE_HEIGHT = 18
const LINES_PER_PAGE = Math.floor((PAGE_HEIGHT_PX - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM) / LINE_HEIGHT)

export default function ScriptEditorPage() {
    const { projectId, scriptId } = useParams<{ projectId: string; scriptId: string }>()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const editorContainerRef = useRef<HTMLDivElement>(null)

    const [title, setTitle] = useState('')
    const [elements, setElements] = useState<ScriptElement[]>([])
    const [selectedElement, setSelectedElement] = useState<string | null>(null)
    const [selectedElements, setSelectedElements] = useState<Set<string>>(new Set())
    const [hasChanges, setHasChanges] = useState(false)

    // Undo/Redo history
    const [history, setHistory] = useState<ScriptElement[][]>([])
    const [historyIndex, setHistoryIndex] = useState(-1)
    const [isUndoRedo, setIsUndoRedo] = useState(false)

    const { data: scriptData, isLoading } = useQuery({
        queryKey: ['script', scriptId],
        queryFn: () => api.get<Script>(`/projects/${projectId}/scripts/${scriptId}`),
        enabled: !!scriptId,
    })

    const saveMutation = useMutation({
        mutationFn: (data: { title: string; content: string }) =>
            api.patch(`/projects/${projectId}/scripts/${scriptId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['script', scriptId] })
            setHasChanges(false)
            toast({ title: 'Saved' })
        },
        onError: (error: any) => {
            toast({ title: 'Save failed', description: error.message, variant: 'destructive' })
        },
    })

    useEffect(() => {
        if (scriptData?.data) {
            setTitle(scriptData.data.title)
            const existingElements = scriptData.data.content?.elements || []
            if (existingElements.length === 0) {
                const initial = [{ id: `el-${Date.now()}`, type: 'action' as ElementType, content: '' }]
                setElements(initial)
                setHistory([initial])
                setHistoryIndex(0)
            } else {
                setElements(existingElements)
                setHistory([existingElements])
                setHistoryIndex(0)
            }
        }
    }, [scriptData])

    // Push to history when elements change (except during undo/redo)
    useEffect(() => {
        if (isUndoRedo) {
            setIsUndoRedo(false)
            return
        }
        if (elements.length > 0 && historyIndex >= 0) {
            const lastState = history[historyIndex]
            if (JSON.stringify(lastState) !== JSON.stringify(elements)) {
                const newHistory = history.slice(0, historyIndex + 1)
                newHistory.push(elements)
                // Keep max 50 history states
                if (newHistory.length > 50) newHistory.shift()
                setHistory(newHistory)
                setHistoryIndex(newHistory.length - 1)
            }
        }
    }, [elements])

    const handleUndo = useCallback(() => {
        if (historyIndex > 0) {
            setIsUndoRedo(true)
            setHistoryIndex(historyIndex - 1)
            setElements(history[historyIndex - 1])
            setHasChanges(true)
        }
    }, [historyIndex, history])

    const handleRedo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            setIsUndoRedo(true)
            setHistoryIndex(historyIndex + 1)
            setElements(history[historyIndex + 1])
            setHasChanges(true)
        }
    }, [historyIndex, history])

    const handleSave = useCallback(() => {
        const content: ScriptContent = {
            elements,
            metadata: {
                title,
                author: scriptData?.data?.author || '',
            },
        }
        saveMutation.mutate({ title, content: JSON.stringify(content) })
    }, [title, elements, scriptData, saveMutation])

    // Keyboard shortcuts
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault()
                handleSave()
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault()
                handleUndo()
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
                e.preventDefault()
                handleRedo()
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
                e.preventDefault()
                handleRedo()
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
                const activeEl = document.activeElement
                if (activeEl?.tagName !== 'TEXTAREA' && activeEl?.tagName !== 'INPUT') {
                    e.preventDefault()
                    setSelectedElements(new Set(elements.map(el => el.id)))
                }
            }
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElements.size > 0) {
                const activeEl = document.activeElement
                if (activeEl?.tagName !== 'TEXTAREA' && activeEl?.tagName !== 'INPUT') {
                    e.preventDefault()
                    deleteSelectedElements()
                }
            }
            if (e.key === 'Escape') {
                setSelectedElements(new Set())
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
                e.preventDefault()
                toggleFormatting('bold')
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
                e.preventDefault()
                toggleFormatting('italic')
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'u') {
                e.preventDefault()
                toggleFormatting('underline')
            }
        }
        window.addEventListener('keydown', handleGlobalKeyDown)
        return () => window.removeEventListener('keydown', handleGlobalKeyDown)
    }, [handleSave, handleUndo, handleRedo, selectedElement, elements, selectedElements])

    const deleteSelectedElements = () => {
        if (selectedElements.size === 0) return

        let newElements = elements.filter(el => !selectedElements.has(el.id))
        if (newElements.length === 0) {
            newElements = [{ id: `el-${Date.now()}`, type: 'action', content: '' }]
        }
        setElements(newElements)
        setSelectedElements(new Set())
        setSelectedElement(newElements[0]?.id || null)
        setHasChanges(true)

        if (newElements[0]) {
            focusElement(newElements[0].id)
        }
    }

    // Toggle formatting (add or remove)
    const toggleFormatting = (format: 'bold' | 'italic' | 'underline' | 'strikethrough') => {
        if (!selectedElement) return

        const textarea = document.querySelector(`[data-element-id="${selectedElement}"]`) as HTMLTextAreaElement
        if (!textarea) return

        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const selectedText = textarea.value.substring(start, end)

        if (start === end) return

        const markers: Record<string, { start: string; end: string }> = {
            bold: { start: '**', end: '**' },
            italic: { start: '*', end: '*' },
            underline: { start: '_', end: '_' },
            strikethrough: { start: '~~', end: '~~' },
        }

        const { start: startMarker, end: endMarker } = markers[format]

        let newText: string
        let newStart: number
        let newEnd: number

        // Check if already formatted - look at surrounding text
        const beforeStart = textarea.value.substring(Math.max(0, start - startMarker.length), start)
        const afterEnd = textarea.value.substring(end, end + endMarker.length)

        if (beforeStart === startMarker && afterEnd === endMarker) {
            // Remove formatting
            newText = textarea.value.substring(0, start - startMarker.length) +
                selectedText +
                textarea.value.substring(end + endMarker.length)
            newStart = start - startMarker.length
            newEnd = newStart + selectedText.length
        } else if (selectedText.startsWith(startMarker) && selectedText.endsWith(endMarker)) {
            // Selection includes markers - remove them
            const unformatted = selectedText.slice(startMarker.length, -endMarker.length)
            newText = textarea.value.substring(0, start) + unformatted + textarea.value.substring(end)
            newStart = start
            newEnd = start + unformatted.length
        } else {
            // Add formatting
            const formatted = `${startMarker}${selectedText}${endMarker}`
            newText = textarea.value.substring(0, start) + formatted + textarea.value.substring(end)
            newStart = start
            newEnd = start + formatted.length
        }

        updateElement(selectedElement, newText)

        requestAnimationFrame(() => {
            textarea.focus()
            textarea.setSelectionRange(newStart, newEnd)
        })
    }

    const detectElementType = (content: string, currentType: ElementType): ElementType => {
        const trimmed = content.trim()
        if (SCENE_HEADING_PATTERN.test(trimmed)) return 'scene_heading'
        if (TRANSITION_PATTERN.test(trimmed)) return 'transition'
        if (currentType === 'dialogue' || currentType === 'parenthetical') return currentType
        if (trimmed === trimmed.toUpperCase() && /[A-Z]{2,}/.test(trimmed)) {
            return 'character'
        }
        return currentType
    }

    const focusElement = (id: string, atEnd = true) => {
        requestAnimationFrame(() => {
            const el = document.querySelector(`[data-element-id="${id}"]`) as HTMLTextAreaElement
            if (el) {
                el.focus()
                if (atEnd) {
                    el.setSelectionRange(el.value.length, el.value.length)
                } else {
                    el.setSelectionRange(0, 0)
                }
            }
        })
    }

    const addElement = (type: ElementType, afterIndex: number, content = '') => {
        const newElement: ScriptElement = {
            id: `el-${Date.now()}`,
            type,
            content,
        }
        const newElements = [...elements]
        newElements.splice(afterIndex + 1, 0, newElement)
        setElements(newElements)
        setSelectedElement(newElement.id)
        setSelectedElements(new Set())
        setHasChanges(true)
        focusElement(newElement.id, content.length === 0)
    }

    const updateElement = (id: string, content: string) => {
        setElements(elements.map(el => {
            if (el.id === id) {
                const newType = detectElementType(content, el.type)
                return { ...el, content, type: newType }
            }
            return el
        }))
        setHasChanges(true)
    }

    const changeElementType = (id: string, type: ElementType) => {
        setElements(elements.map(el =>
            el.id === id ? { ...el, type } : el
        ))
        setHasChanges(true)
        focusElement(id)
    }

    const handleElementClick = (e: React.MouseEvent, elementId: string, index: number) => {
        if (e.shiftKey && selectedElement) {
            const currentIndex = elements.findIndex(el => el.id === selectedElement)
            const start = Math.min(currentIndex, index)
            const end = Math.max(currentIndex, index)
            const newSelection = new Set<string>()
            for (let i = start; i <= end; i++) {
                newSelection.add(elements[i].id)
            }
            setSelectedElements(newSelection)
        } else if (e.metaKey || e.ctrlKey) {
            const newSelection = new Set(selectedElements)
            if (newSelection.has(elementId)) {
                newSelection.delete(elementId)
            } else {
                newSelection.add(elementId)
            }
            setSelectedElements(newSelection)
        } else {
            setSelectedElements(new Set())
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, elementId: string, index: number) => {
        const currentEl = elements[index]
        const textarea = e.target as HTMLTextAreaElement
        const cursorPos = textarea.selectionStart || 0
        const selectionEnd = textarea.selectionEnd || 0

        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            if (currentEl.type === 'character' && currentEl.content.trim() !== '') {
                addElement('parenthetical', index)
                return
            }
            const currentTypeIndex = TAB_CYCLE.indexOf(currentEl.type)
            const nextIndex = currentTypeIndex === -1 ? 0 : (currentTypeIndex + 1) % TAB_CYCLE.length
            changeElementType(elementId, TAB_CYCLE[nextIndex])
            return
        }

        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault()
            const currentTypeIndex = TAB_CYCLE.indexOf(currentEl.type)
            const prevIndex = currentTypeIndex <= 0 ? TAB_CYCLE.length - 1 : currentTypeIndex - 1
            changeElementType(elementId, TAB_CYCLE[prevIndex])
            return
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            let nextType: ElementType = 'action'
            if (currentEl.type === 'character') nextType = 'dialogue'
            else if (currentEl.type === 'dialogue') nextType = 'character'
            else if (currentEl.type === 'parenthetical') nextType = 'dialogue'
            else if (currentEl.type === 'scene_heading') nextType = 'action'
            else if (currentEl.type === 'transition') nextType = 'scene_heading'

            if (cursorPos < currentEl.content.length) {
                const beforeCursor = currentEl.content.slice(0, cursorPos)
                const afterCursor = currentEl.content.slice(cursorPos)
                setElements(elements.map(el =>
                    el.id === elementId ? { ...el, content: beforeCursor } : el
                ))
                addElement(currentEl.type, index, afterCursor)
            } else {
                addElement(nextType, index)
            }
            return
        }

        if (e.key === 'Backspace' && cursorPos === 0 && selectionEnd === 0) {
            if (currentEl.content === '' && elements.length > 1) {
                e.preventDefault()
                const newElements = elements.filter((_, i) => i !== index)
                setElements(newElements)
                setHasChanges(true)
                const prevIndex = Math.max(0, index - 1)
                const prevElement = newElements[prevIndex]
                if (prevElement) {
                    setSelectedElement(prevElement.id)
                    focusElement(prevElement.id)
                }
            } else if (index > 0 && currentEl.content.length > 0) {
                e.preventDefault()
                const prevElement = elements[index - 1]
                const mergedContent = prevElement.content + currentEl.content
                const cursorAtMerge = prevElement.content.length
                const newElements = elements.filter((_, i) => i !== index).map(el =>
                    el.id === prevElement.id ? { ...el, content: mergedContent } : el
                )
                setElements(newElements)
                setHasChanges(true)
                setSelectedElement(prevElement.id)
                requestAnimationFrame(() => {
                    const el = document.querySelector(`[data-element-id="${prevElement.id}"]`) as HTMLTextAreaElement
                    if (el) {
                        el.focus()
                        el.setSelectionRange(cursorAtMerge, cursorAtMerge)
                    }
                })
            }
            return
        }

        if (e.key === 'Delete' && cursorPos === currentEl.content.length && index < elements.length - 1) {
            e.preventDefault()
            const nextElement = elements[index + 1]
            const mergedContent = currentEl.content + nextElement.content
            const newElements = elements.filter((_, i) => i !== index + 1).map(el =>
                el.id === elementId ? { ...el, content: mergedContent } : el
            )
            setElements(newElements)
            setHasChanges(true)
            return
        }

        if (e.key === 'ArrowUp' && cursorPos === 0 && index > 0) {
            e.preventDefault()
            const prevElement = elements[index - 1]
            setSelectedElement(prevElement.id)
            focusElement(prevElement.id)
        }

        if (e.key === 'ArrowDown' && cursorPos === currentEl.content.length && index < elements.length - 1) {
            e.preventDefault()
            const nextElement = elements[index + 1]
            setSelectedElement(nextElement.id)
            focusElement(nextElement.id, false)
        }

        if (e.key === '(' && currentEl.type === 'dialogue' && currentEl.content === '') {
            e.preventDefault()
            changeElementType(elementId, 'parenthetical')
        }
    }

    const handleTextareaInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
        const textarea = e.target as HTMLTextAreaElement
        textarea.style.height = 'auto'
        textarea.style.height = textarea.scrollHeight + 'px'
    }

    const getElementContainerStyles = (type: ElementType): string => {
        switch (type) {
            case 'character':
            case 'parenthetical':
            case 'dialogue':
                return 'flex justify-center'
            default:
                return ''
        }
    }

    const getElementStyles = (type: ElementType): string => {
        switch (type) {
            case 'scene_heading':
                return 'uppercase font-bold tracking-wide w-full'
            case 'action':
                return 'w-full'
            case 'character':
                return 'uppercase text-center w-[40%]'
            case 'dialogue':
                return 'text-center w-[60%]'
            case 'parenthetical':
                return 'text-center italic text-sm w-[35%]'
            case 'transition':
                return 'uppercase text-right w-full'
            case 'centered':
                return 'text-center w-full'
            default:
                return 'w-full'
        }
    }

    const getPlaceholder = (type: ElementType): string => {
        switch (type) {
            case 'scene_heading': return 'INT. LOCATION - DAY'
            case 'action': return 'Action...'
            case 'character': return 'CHARACTER'
            case 'dialogue': return 'Dialogue...'
            case 'parenthetical': return '(wryly)'
            case 'transition': return 'CUT TO:'
            default: return ''
        }
    }

    // Calculate estimated lines per element
    const getElementLines = (element: ScriptElement): number => {
        const charsPerLine = 60
        const lines = Math.ceil(Math.max(1, element.content.length) / charsPerLine)
        return lines + 1 // +1 for spacing
    }

    // Paginate elements into fixed-size pages
    const paginateElements = (): ScriptElement[][] => {
        const pages: ScriptElement[][] = []
        let currentPage: ScriptElement[] = []
        let currentLines = 0

        for (const element of elements) {
            const elementLines = getElementLines(element)

            if (currentLines + elementLines > LINES_PER_PAGE && currentPage.length > 0) {
                pages.push(currentPage)
                currentPage = []
                currentLines = 0
            }

            currentPage.push(element)
            currentLines += elementLines
        }

        if (currentPage.length > 0) {
            pages.push(currentPage)
        }

        if (pages.length === 0) {
            pages.push([])
        }

        return pages
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    const script = scriptData?.data

    if (!script) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-background">
                <p className="text-muted-foreground mb-4">Script not found</p>
                <Button onClick={() => navigate(`/projects/${projectId}`)}>Back to Project</Button>
            </div>
        )
    }

    const pages = paginateElements()
    const currentElement = elements.find(el => el.id === selectedElement)
    const wordCount = elements.reduce((acc, el) => acc + el.content.split(/\s+/).filter(Boolean).length, 0)
    const currentPage = pages.findIndex(page => page.some(el => el.id === selectedElement)) + 1

    return (
        <div className="h-full flex flex-col bg-background">
            {/* Title bar */}
            <div className="flex items-center justify-between px-4 py-2 border-b bg-card/50">
                <div className="flex items-center gap-3">
                    <Input
                        value={title}
                        onChange={(e) => { setTitle(e.target.value); setHasChanges(true) }}
                        className="text-sm font-medium border-none bg-transparent w-48 focus-visible:ring-0 px-1"
                    />
                    {hasChanges && (
                        <span className="text-xs text-primary animate-pulse">• Unsaved</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {selectedElements.size > 0 && (
                        <span className="text-xs text-muted-foreground">{selectedElements.size} selected</span>
                    )}
                    <Button variant="ghost" size="sm" className="h-8" title="History">
                        <History className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8" title="Export">
                        <FileDown className="h-4 w-4" />
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saveMutation.isPending || !hasChanges}
                    >
                        {saveMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-1" />
                                Save
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Main Editor - Dark Pages */}
            <main className="flex-1 overflow-auto pb-20" ref={editorContainerRef}>
                <div className="flex flex-col items-center py-6 gap-6">
                    {pages.map((pageElements, pageIndex) => (
                        <div
                            key={pageIndex}
                            className="bg-zinc-900 border border-zinc-800 shadow-2xl relative flex-shrink-0 rounded"
                            style={{
                                width: `${PAGE_WIDTH_PX}px`,
                                minHeight: `${PAGE_HEIGHT_PX}px`,
                                fontFamily: "'Courier Prime', 'Courier New', Courier, monospace",
                            }}
                        >
                            {/* Page number */}
                            <div
                                className="absolute text-xs text-zinc-600"
                                style={{ top: `${PAGE_PADDING_TOP / 2}px`, right: `${PAGE_PADDING_RIGHT}px` }}
                            >
                                {pageIndex + 1}.
                            </div>

                            {/* Page content */}
                            <div
                                className="overflow-hidden"
                                style={{
                                    padding: `${PAGE_PADDING_TOP}px ${PAGE_PADDING_RIGHT}px ${PAGE_PADDING_BOTTOM}px ${PAGE_PADDING_LEFT}px`,
                                    minHeight: `${PAGE_HEIGHT_PX}px`,
                                }}
                            >
                                {pageElements.map((element) => {
                                    const globalIndex = elements.findIndex(el => el.id === element.id)
                                    return (
                                        <div
                                            key={element.id}
                                            className={cn(
                                                "relative group transition-all duration-75 cursor-text",
                                                getElementContainerStyles(element.type),
                                                selectedElement === element.id && "bg-zinc-800/60 -mx-2 px-2 rounded",
                                                selectedElements.has(element.id) && "bg-primary/20 -mx-2 px-2 rounded ring-1 ring-primary/50"
                                            )}
                                            onClick={(e) => handleElementClick(e, element.id, globalIndex)}
                                        >
                                            {selectedElement === element.id && (
                                                <div className="absolute -top-3 left-0 text-[9px] text-primary font-sans">
                                                    {ELEMENT_LABELS[element.type]}
                                                </div>
                                            )}

                                            <textarea
                                                data-element-id={element.id}
                                                value={element.content}
                                                onChange={(e) => updateElement(element.id, e.target.value)}
                                                onFocus={() => {
                                                    setSelectedElement(element.id)
                                                    if (!selectedElements.has(element.id)) {
                                                        setSelectedElements(new Set())
                                                    }
                                                }}
                                                onKeyDown={(e) => handleKeyDown(e, element.id, globalIndex)}
                                                onInput={handleTextareaInput}
                                                placeholder={getPlaceholder(element.type)}
                                                rows={1}
                                                className={cn(
                                                    "bg-transparent border-none outline-none resize-none text-[12pt] leading-tight py-0.5 text-zinc-100 placeholder:text-zinc-700",
                                                    getElementStyles(element.type)
                                                )}
                                                autoComplete="off"
                                                spellCheck={true}
                                                style={{ minHeight: '1.2em', overflow: 'hidden' }}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            {/* Bottom Toolbar */}
            <footer className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
                <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl px-3 py-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        className={cn("h-8 w-8", historyIndex > 0 ? "text-zinc-400 hover:text-white" : "text-zinc-600")}
                        title="Undo (⌘Z)"
                        onClick={handleUndo}
                        disabled={historyIndex <= 0}
                    >
                        <Undo className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className={cn("h-8 w-8", historyIndex < history.length - 1 ? "text-zinc-400 hover:text-white" : "text-zinc-600")}
                        title="Redo (⌘⇧Z)"
                        onClick={handleRedo}
                        disabled={historyIndex >= history.length - 1}
                    >
                        <Redo className="h-4 w-4" />
                    </Button>

                    <div className="w-px h-5 bg-zinc-700 mx-1" />

                    {(['scene_heading', 'action', 'character', 'dialogue', 'parenthetical', 'transition'] as ElementType[]).map((type) => (
                        <Button
                            key={type}
                            variant="ghost"
                            size="sm"
                            className={cn(
                                "h-7 px-2 text-xs",
                                currentElement?.type === type
                                    ? "bg-zinc-700 text-white"
                                    : "text-zinc-400 hover:text-white"
                            )}
                            onClick={() => {
                                if (selectedElement) {
                                    changeElementType(selectedElement, type)
                                }
                            }}
                        >
                            {ELEMENT_LABELS[type]}
                        </Button>
                    ))}

                    <div className="w-px h-5 bg-zinc-700 mx-1" />

                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-400 hover:text-white"
                        title="Bold (⌘B)"
                        onClick={() => toggleFormatting('bold')}
                    >
                        <Bold className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-400 hover:text-white"
                        title="Italic (⌘I)"
                        onClick={() => toggleFormatting('italic')}
                    >
                        <Italic className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-400 hover:text-white"
                        title="Underline (⌘U)"
                        onClick={() => toggleFormatting('underline')}
                    >
                        <Underline className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-400 hover:text-white"
                        title="Strikethrough"
                        onClick={() => toggleFormatting('strikethrough')}
                    >
                        <Strikethrough className="h-4 w-4" />
                    </Button>

                    <div className="w-px h-5 bg-zinc-700 mx-1" />

                    <div className="text-xs text-zinc-500 px-2">
                        {wordCount}w • Page {currentPage}/{pages.length}
                    </div>

                    <div className="w-px h-5 bg-zinc-700 mx-1" />

                    <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white" title="Comments">
                        <MessageSquare className="h-4 w-4" />
                    </Button>
                </div>
            </footer>
        </div>
    )
}
